<template>
  <div v-if="podeConsultar" class="estoque-grupo-produto">
    <button
      type="button"
      class="btn btn-primary btn-sm d-inline-flex align-items-center"
      aria-label="Consultar estoque nas empresas"
      title="Consultar estoque nas empresas (F9)"
      v-b-tooltip.hover.top
      @click="abrir"
    >
      <Magnify :size="18" />
      <span class="ml-2 font-size-xs font-weight-bolder">– F9</span>
    </button>

    <b-modal
      ref="modal"
      title="Estoque do produto nas empresas"
      hide-footer
      scrollable
      :hide-header-close="false"
      :no-close-on-esc="false"
      modal-class="estoque-grupo-modal"
      dialog-class="estoque-grupo-dialog"
      content-class="estoque-grupo-conteudo"
      @hidden="limparPesquisa"
    >
      <div v-if="carregando" class="text-center py-12">
        <b-spinner variant="primary" />
        <div class="text-muted mt-3">Consultando os estoques...</div>
      </div>

      <b-alert v-else-if="erroConsulta" show variant="light-danger">
        <div class="d-flex align-items-center justify-content-between">
          <span>{{ erroConsulta }}</span>
          <b-button size="sm" variant="danger" @click="consultar">
            Tentar novamente
          </b-button>
        </div>
      </b-alert>

      <template v-else-if="consulta">
        <div class="produto-resumo rounded p-4 mb-5">
          <div class="text-muted font-size-sm">Produto selecionado</div>
          <strong>
            {{ consulta.produto_origem.codigo }} -
            {{ consulta.produto_origem.descricao }}
          </strong>
          <div class="text-muted font-size-sm mt-1">
            A consulta é somente leitura e não transfere estoque entre empresas.
          </div>
        </div>

        <div class="table-responsive border rounded">
          <table class="table table-head-custom table-vertical-center mb-0">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Produto localizado</th>
                <th>Código de barras</th>
                <th>Referência</th>
                <th>Marca</th>
                <th class="text-right">Estoque</th>
                <th class="text-right">Reservado</th>
                <th class="text-right">Disponível</th>
                <th class="text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in consulta.empresas" :key="item.empresa.id_empresa">
                <td>
                  <strong>{{ item.empresa.nome }}</strong>
                  <div class="mt-1">
                    <span class="label label-inline" :class="classeStatus(item.status)">
                      {{ textoStatus(item) }}
                    </span>
                  </div>
                </td>
                <td>
                  <template v-if="item.produto">
                    <strong>{{ item.produto.codigo }}</strong>
                    - {{ item.produto.descricao }}
                  </template>
                  <span v-else class="text-muted">Nenhum produto relacionado</span>
                </td>
                <td class="text-nowrap">
                  {{ item.produto && item.produto.cod_barras ? item.produto.cod_barras : "—" }}
                </td>
                <td class="text-nowrap">
                  {{ item.produto && item.produto.referencia ? item.produto.referencia : "—" }}
                </td>
                <td>
                  {{ item.produto && item.produto.marca ? item.produto.marca : "—" }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ item.produto ? formatarQuantidade(item.produto.estoque) : "—" }}
                </td>
                <td class="text-right">
                  {{ item.produto ? formatarQuantidade(item.produto.reservado) : "—" }}
                </td>
                <td
                  class="text-right font-weight-bolder"
                  :class="classeDisponivel(item.produto)"
                >
                  {{ item.produto ? formatarQuantidade(item.produto.disponivel) : "—" }}
                </td>
                <td class="text-right text-nowrap">
                  <button
                    v-if="item.status !== 'ORIGEM'"
                    type="button"
                    class="btn btn-light-primary btn-sm"
                    @click="iniciarPesquisa(item)"
                  >
                    {{ item.status === "VINCULADO" ? "Alterar vínculo" : "Pesquisar produto" }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="empresaPesquisa" class="pesquisa-avancada border rounded p-5 mt-5">
          <div class="d-flex align-items-start justify-content-between mb-4">
            <div>
              <strong>Pesquisar em {{ empresaPesquisa.empresa.nome }}</strong>
              <div class="text-muted font-size-sm">
                Localize por código, código de barras, referência ou descrição.
              </div>
            </div>
            <button type="button" class="btn btn-icon btn-light btn-sm" @click="limparPesquisa">
              ×
            </button>
          </div>

          <b-input-group>
            <b-form-input
              v-model.trim="termoPesquisa"
              placeholder="Digite ao menos 2 caracteres"
              @keyup.enter="pesquisar"
            />
            <b-input-group-append>
              <b-button
                variant="primary"
                :disabled="termoPesquisa.length < 2 || pesquisando"
                @click="pesquisar"
              >
                {{ pesquisando ? "Buscando..." : "Buscar" }}
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div v-if="pesquisaExecutada" class="mt-4">
            <div v-if="!resultados.length" class="text-muted text-center py-5">
              Nenhum produto encontrado nesta empresa.
            </div>
            <div v-else class="resultados-pesquisa">
              <div
                v-for="resultado in resultados"
                :key="resultado.id"
                class="resultado-item d-flex align-items-center justify-content-between border rounded p-3 mb-2"
              >
                <div class="mr-4">
                  <strong>{{ resultado.codigo }} - {{ resultado.descricao }}</strong>
                  <div class="text-muted font-size-sm">
                    <span v-if="resultado.referencia">Ref.: {{ resultado.referencia }} · </span>
                    Disponível: {{ formatarQuantidade(resultado.disponivel) }}
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="vinculando === resultado.id"
                  @click="vincular(resultado)"
                >
                  {{ vinculando === resultado.id ? "Vinculando..." : "Vincular" }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Magnify from "vue-material-design-icons/Magnify.vue";

export default {
  name: "EstoqueGrupoProduto",
  components: {
    Magnify,
  },
  props: {
    produto: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      carregando: false,
      erroConsulta: "",
      consulta: null,
      empresaPesquisa: null,
      termoPesquisa: "",
      resultados: [],
      pesquisando: false,
      pesquisaExecutada: false,
      vinculando: null,
    };
  },
  computed: {
    ...mapGetters(["empresaContexto", "empresas"]),
    empresasDoGrupo() {
      const grupoId = Number(this.empresaContexto?.id_grupo_empresa || 0);

      return (this.empresas || []).filter(
        (empresa) => Number(empresa.id_grupo_empresa || 0) === grupoId
      );
    },
    podeConsultar() {
      return (
        Number(this.produto?.id || 0) > 0 &&
        !this.produto?.isKit &&
        Number(this.empresaContexto?.id_grupo_empresa || 0) > 0 &&
        this.empresasDoGrupo.length > 1
      );
    },
  },
  watch: {
    "produto.id"() {
      this.consulta = null;
      this.erroConsulta = "";
      this.limparPesquisa();
    },
  },
  mounted() {
    window.addEventListener("keydown", this.abrirPeloAtalho);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.abrirPeloAtalho);
  },
  methods: {
    abrirPeloAtalho(event) {
      if (event.key !== "F9" || event.repeat || !this.podeConsultar) return;

      event.preventDefault();
      event.stopPropagation();
      this.abrir();
    },
    async abrir() {
      this.$refs.modal.show();
      await this.consultar();
    },
    async consultar() {
      this.carregando = true;
      this.erroConsulta = "";

      try {
        const response = await this.$http.post("/empresa/grupo/estoques/produto", {
          id_produto: Number(this.produto.id),
        });
        this.consulta = response.data.data;
      } catch (error) {
        this.consulta = null;
        this.erroConsulta =
          error?.response?.data?.message ||
          "Não foi possível consultar os estoques agora.";
      } finally {
        this.carregando = false;
      }
    },
    iniciarPesquisa(item) {
      this.empresaPesquisa = item;
      this.termoPesquisa = this.produto?.referencia || this.produto?.cod_barras || "";
      this.resultados = [];
      this.pesquisaExecutada = false;
    },
    limparPesquisa() {
      this.empresaPesquisa = null;
      this.termoPesquisa = "";
      this.resultados = [];
      this.pesquisaExecutada = false;
      this.pesquisando = false;
    },
    async pesquisar() {
      if (!this.empresaPesquisa || this.termoPesquisa.length < 2 || this.pesquisando) {
        return;
      }

      this.pesquisando = true;

      try {
        const response = await this.$http.post("/empresa/grupo/estoques/pesquisar", {
          id_produto_origem: Number(this.produto.id),
          id_empresa_destino: Number(this.empresaPesquisa.empresa.id_empresa),
          busca: this.termoPesquisa,
        });
        this.resultados = response.data.data || [];
        this.pesquisaExecutada = true;
      } finally {
        this.pesquisando = false;
      }
    },
    async vincular(produtoDestino) {
      if (!this.empresaPesquisa || this.vinculando) return;

      this.vinculando = produtoDestino.id;

      try {
        const response = await this.$http.post("/empresa/grupo/estoques/vincular", {
          id_produto_origem: Number(this.produto.id),
          id_empresa_destino: Number(this.empresaPesquisa.empresa.id_empresa),
          id_produto_destino: Number(produtoDestino.id),
        });
        this.consulta = response.data.data;
        this.limparPesquisa();

        if (this.$toast) {
          this.$toast.success("Produto vinculado à empresa com sucesso.");
        }
      } finally {
        this.vinculando = null;
      }
    },
    textoStatus(item) {
      const textos = {
        ORIGEM: "Empresa atual",
        VINCULADO: "Vínculo manual",
        LOCALIZADO:
          item.metodo === "COD_BARRAS" ? "Mesmo código de barras" : "Mesma referência",
        AMBIGUO: "Mais de um resultado",
        NAO_LOCALIZADO: "Não localizado",
      };

      return textos[item.status] || item.status;
    },
    classeStatus(status) {
      if (["ORIGEM", "VINCULADO"].includes(status)) return "label-light-primary";
      if (status === "LOCALIZADO") return "label-light-success";
      if (["AMBIGUO", "NAO_LOCALIZADO"].includes(status)) return "label-warning";
      return "label-light-secondary";
    },
    classeDisponivel(produto) {
      if (!produto) return "";
      return Number(produto.disponivel) > 0 ? "text-success" : "text-danger";
    },
    formatarQuantidade(valor) {
      return Number(valor || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 3,
      });
    },
  },
};
</script>

<style scoped>
.estoque-grupo-produto {
  display: inline-block;
  flex: 0 0 auto;
  margin-left: 0.35rem;
}

.produto-resumo,
.pesquisa-avancada {
  background: #f8fafc;
}

.table td,
.table th {
  vertical-align: middle;
}

.resultado-item {
  background: #fff;
}

@media (max-width: 767px) {
  .resultado-item {
    align-items: flex-start !important;
    flex-direction: column;
  }

  .resultado-item .btn {
    margin-top: 0.75rem;
    width: 100%;
  }
}
</style>

<style>
.estoque-grupo-modal {
  overflow: hidden;
  padding: 0 !important;
}

.estoque-grupo-modal .estoque-grupo-dialog {
  display: flex;
  height: calc(100vh - 2rem);
  left: 0;
  margin: 0;
  max-width: none;
  position: fixed;
  right: 0;
  bottom: 0;
  width: 100%;
}

.estoque-grupo-modal .estoque-grupo-conteudo {
  border: 0;
  border-radius: 1rem 1rem 0 0;
  height: 100%;
  max-height: 100%;
  width: 100%;
}

.estoque-grupo-modal .modal-body {
  overflow-y: auto;
}

.estoque-grupo-modal.fade .modal-dialog {
  transform: translateY(100%);
}

.estoque-grupo-modal.show .modal-dialog {
  transform: translateY(0);
}

@media (max-width: 767px) {
  .estoque-grupo-modal .estoque-grupo-dialog {
    height: calc(100vh - 1rem);
  }
}
</style>
